/**
 * Grid card components for the Trace Studio panel (packs, assets, folders,
 * projects, scenes, and the dashed "new"/"publish" action cards), plus the
 * always-visible card badges (publish dot, location type, scene count).
 */

import React, { useState } from 'react';
import type { CatalogResponseDto, FolderTeamResponseDto, ProjectResponseDto, SceneResponseDto } from 'trace-viewer/cms';
import { styles } from './catalog-styles';
import { resolveUrl, normalizeStatus, PUBLISH_STATUS_LABELS, type PublishStatus, type AssetItem } from './catalog-api';
import { isLocationBased } from './catalog-filters';

// Card layout: grow to fill row, cap at max width, spacers prevent last-row stretch.
const CARD_STYLE: React.CSSProperties = { flex: '1 1 120px', maxWidth: '300px', minWidth: '120px' };
const SPACER_STYLE: React.CSSProperties = { ...CARD_STYLE, height: 0, padding: 0, margin: 0, overflow: 'hidden' };
// Fixed-size thumbnail area so cards don't jump as images load.
const THUMB_STYLE: React.CSSProperties = { width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block', backgroundColor: 'var(--colorNeutralBackground2)' };
const THUMB_PLACEHOLDER: React.CSSProperties = { width: '100%', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--colorNeutralBackground2)' };
const NUM_SPACERS = 4;
export const Spacers = () => <>{Array.from({ length: NUM_SPACERS }, (_, i) => <div key={`sp-${i}`} style={SPACER_STYLE} />)}</>;

// ============================================================================
// Base card
// ============================================================================

/** Shared base card used by all entity cards (packs, assets, folders, projects, scenes, publish). */
const EntityCard: React.FC<{
    /** Custom thumbnail node (e.g. tile collage); takes precedence over thumbnailUrl. */
    thumbnail?: React.ReactNode;
    thumbnailUrl?: string | null;
    thumbnailAlt?: string;
    thumbnailPlaceholder?: React.ReactNode;
    label: string;
    selected?: boolean;
    inserting?: boolean;
    dashed?: boolean;
    brandOnHover?: boolean;
    hoverOverlay?: React.ReactNode;
    /** Always-visible ornaments rendered over the thumbnail (status dot, type icon). */
    badges?: React.ReactNode;
    onClick?: () => void;
}> = ({ thumbnail, thumbnailUrl, thumbnailAlt = '', thumbnailPlaceholder, label, selected, inserting, dashed, brandOnHover, hoverOverlay, badges, onClick }) => {
    const [hovered, setHovered] = useState(false);
    // Selection-capable cards (selected !== undefined) use softer hover; navigation cards use brand hover
    const selectable = selected !== undefined;
    const borderColor = selected
        ? 'var(--colorBrandBackground)'
        : hovered
            ? (selectable ? 'var(--colorNeutralStroke1)' : 'var(--colorBrandBackground)')
            : 'transparent';

    return (
        <div
            style={{
                ...styles.card,
                ...CARD_STYLE,
                borderColor,
                ...(dashed ? { borderStyle: 'dashed' as const } : {}),
                opacity: inserting ? 0.5 : 1,
                pointerEvents: inserting ? 'none' : 'auto',
            }}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            aria-label={onClick ? label : undefined}
            onClick={onClick}
            onKeyDown={onClick ? (e => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick();
                }
            }) : undefined}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <div style={{ position: 'relative' }}>
                {thumbnail ?? (thumbnailUrl ? (
                    <img src={thumbnailUrl} alt={thumbnailAlt} style={THUMB_STYLE} crossOrigin="anonymous" />
                ) : (
                    <div style={THUMB_PLACEHOLDER}>
                        {thumbnailPlaceholder}
                    </div>
                ))}
                {badges}
                {hovered && hoverOverlay}
            </div>
            <div style={{
                ...styles.cardLabel,
                ...(brandOnHover && hovered ? { color: 'var(--colorBrandBackground)' } : {}),
            }}>
                {label}
            </div>
        </div>
    );
};

const INITIAL_STYLE: React.CSSProperties = { fontSize: '18px', fontWeight: 700, color: 'var(--colorNeutralForeground4)' };
const ICON_COLOR: React.CSSProperties = { color: 'var(--colorNeutralForeground4)' };

export const CubeSvg: React.FC<{ size?: number }> = ({ size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={ICON_COLOR}>
        <path d="M21 16.5c0 .38-.21.71-.53.88l-7.9 4.44c-.16.12-.36.18-.57.18-.21 0-.41-.06-.57-.18l-7.9-4.44A.991.991 0 013 16.5v-9c0-.38.21-.71.53-.88l7.9-4.44c.16-.12.36-.18.57-.18.21 0 .41.06.57.18l7.9 4.44c.32.17.53.5.53.88v9zM12 4.15L5 8.09v7.82l7 3.94 7-3.94V8.09l-7-3.94z"/>
    </svg>
);

const IMPORT_OVERLAY_STYLE: React.CSSProperties = {
    position: 'absolute', top: '4px', right: '4px',
    width: '24px', height: '24px', padding: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    backgroundColor: 'var(--colorBrandBackground)',
    color: 'var(--colorNeutralForegroundOnBrand)',
    border: 'none', borderRadius: '4px', cursor: 'pointer',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
};

// ============================================================================
// Card badges (publish status dot, location/anywhere type icon, scene count)
// ============================================================================

const PUBLISH_DOT_COLORS: Partial<Record<PublishStatus, string>> = {
    Published: '#4CAF50',
    PublishedWithLimitedAccess: '#4CAF50',
    OpenPreview: '#FF9800',
};

/** Upper-right dot: green = published, orange = open preview, hidden when unpublished. */
const PublishStatusDot: React.FC<{ status: string | null | undefined }> = ({ status }) => {
    const normalized = normalizeStatus(status);
    const color = PUBLISH_DOT_COLORS[normalized];
    if (!color) return null;
    return (
        <div
            title={PUBLISH_STATUS_LABELS[normalized]}
            style={{
                position: 'absolute', top: '5px', right: '5px',
                width: '8px', height: '8px', borderRadius: '50%',
                backgroundColor: color,
                boxShadow: '0 0 0 1.5px rgba(0,0,0,0.45)',
            }}
        />
    );
};

/** Upper-left icon: placemark for location-based (LBX), globe for view-anywhere. */
const EntityTypeBadge: React.FC<{ locationBased: boolean }> = ({ locationBased }) => (
    <div
        title={locationBased ? 'Location-based' : 'Anywhere'}
        style={{
            position: 'absolute', top: '4px', left: '4px',
            width: '18px', height: '18px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '4px',
            backgroundColor: 'rgba(0,0,0,0.5)', color: '#fff',
        }}
    >
        {locationBased ? (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                {/* Placemark / map pin */}
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z"/>
            </svg>
        ) : (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                {/* Globe */}
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
            </svg>
        )}
    </div>
);

/** Lower-right scene-count chip for project cards. */
const SceneCountChip: React.FC<{ count: number }> = ({ count }) => (
    <div
        title={`${count} scene${count === 1 ? '' : 's'}`}
        style={{
            position: 'absolute', bottom: '4px', right: '4px',
            padding: '1px 5px', borderRadius: '8px',
            fontSize: '9px', fontWeight: 600, lineHeight: '12px',
            backgroundColor: 'rgba(0,0,0,0.55)', color: '#fff',
        }}
    >
        {count}
    </div>
);

// ============================================================================
// Entity cards
// ============================================================================

/** 2x2 collage of asset thumbnails used as the pack icon. */
const PackTilePreview: React.FC<{ urls: string[]; alt: string }> = ({ urls, alt }) => (
    <div style={{
        width: '100%', aspectRatio: '1', display: 'grid',
        gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: '1px',
        backgroundColor: 'var(--colorNeutralBackground2)',
    }}>
        {Array.from({ length: 4 }, (_, i) => urls[i] ? (
            <img
                key={i}
                src={urls[i]}
                alt={i === 0 ? alt : ''}
                crossOrigin="anonymous"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', minWidth: 0, minHeight: 0 }}
            />
        ) : (
            <div key={i} style={{ backgroundColor: 'var(--colorNeutralBackground2)' }} />
        ))}
    </div>
);

export const PackCard: React.FC<{ pack: CatalogResponseDto; onClick: () => void }> = ({ pack, onClick }) => {
    // Pack icon: 2x2 collage of the first four preview assets that have
    // thumbnails; falls back to the pack's own icon, then its initial.
    const tileUrls = (pack.catalogAssets ?? [])
        .map(a => resolveUrl(a.thumbnailUrl))
        .filter((u): u is string => !!u)
        .slice(0, 4);
    return (
        <EntityCard
            thumbnail={tileUrls.length > 0 ? <PackTilePreview urls={tileUrls} alt={pack.name ?? ''} /> : undefined}
            thumbnailUrl={resolveUrl(pack.icon)}
            thumbnailAlt={pack.name ?? ''}
            thumbnailPlaceholder={<div style={INITIAL_STYLE}>{(pack.name ?? 'P')[0].toUpperCase()}</div>}
            label={pack.name ?? 'Untitled Pack'}
            onClick={onClick}
        />
    );
};

export const AssetCard: React.FC<{ asset: AssetItem; onSelect?: () => void; onImport?: () => void; selected?: boolean; inserting: boolean }> = ({ asset, onSelect, onImport, selected, inserting }) => (
    <EntityCard
        thumbnailUrl={resolveUrl(asset.thumbnailUrl)}
        thumbnailAlt={asset.title ?? ''}
        thumbnailPlaceholder={<CubeSvg />}
        label={asset.title ?? asset.originalFilename ?? 'Asset'}
        selected={selected}
        inserting={inserting}
        hoverOverlay={onImport && !inserting ? (
            <button
                style={IMPORT_OVERLAY_STYLE}
                title="Import into scene"
                onClick={e => { e.stopPropagation(); onImport(); }}
            >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M8 3v10M3 8h10"/>
                </svg>
            </button>
        ) : undefined}
        onClick={onSelect}
    />
);

export const FolderCard: React.FC<{ folder: FolderTeamResponseDto; onClick: () => void }> = ({ folder, onClick }) => (
    <EntityCard
        thumbnailPlaceholder={
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style={ICON_COLOR}>
                <path d="M10 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/>
            </svg>
        }
        label={folder.title ?? 'Untitled Folder'}
        onClick={onClick}
    />
);

export const ProjectCard: React.FC<{ project: ProjectResponseDto; sceneCount?: number; onClick: () => void }> = ({ project, sceneCount, onClick }) => (
    <EntityCard
        thumbnailUrl={resolveUrl(project.logoUrl)}
        thumbnailAlt={project.name ?? ''}
        thumbnailPlaceholder={<div style={INITIAL_STYLE}>{(project.name ?? 'P')[0].toUpperCase()}</div>}
        label={project.name ?? 'Untitled Project'}
        badges={(project.id || sceneCount != null) ? <>
            {project.id && <>
                <EntityTypeBadge locationBased={isLocationBased(project.projectAccessors)} />
                <PublishStatusDot status={project.activationStatus} />
            </>}
            {sceneCount != null && <SceneCountChip count={sceneCount} />}
        </> : undefined}
        onClick={onClick}
    />
);

export const SceneCard: React.FC<{ scene: SceneResponseDto; onSelect?: () => void; selected?: boolean; inserting: boolean }> = ({ scene, onSelect, selected, inserting }) => (
    <EntityCard
        thumbnailUrl={resolveUrl(scene.imageUrl)}
        thumbnailAlt={scene.name ?? ''}
        thumbnailPlaceholder={<CubeSvg />}
        label={scene.name ?? 'Untitled Scene'}
        selected={selected}
        inserting={inserting}
        badges={scene.id ? <>
            <EntityTypeBadge locationBased={isLocationBased(scene.sceneAccessors)} />
            <PublishStatusDot status={scene.activationStatus} />
        </> : undefined}
        onClick={onSelect}
    />
);

export const NewFolderCard: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <EntityCard
        thumbnailPlaceholder={
            <svg width="36" height="36" viewBox="0 0 24 24" fill="currentColor" style={ICON_COLOR}>
                {/* Folder with plus — Material Design "create new folder" */}
                <path d="M20 6h-8l-2-2H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-1 8h-3v3h-2v-3h-3v-2h3V9h2v3h3v2z"/>
            </svg>
        }
        label="New Folder"
        dashed
        brandOnHover
        onClick={onClick}
    />
);

export const NewSceneCard: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <EntityCard
        thumbnailPlaceholder={
            <svg width="36" height="36" viewBox="0 0 24 24" fill="currentColor" style={ICON_COLOR}>
                {/* Cube with plus — "new scene" */}
                <path d="M12 2 3 7v10l9 5 9-5V7l-9-5zm0 2.236L18.764 8 12 11.764 5.236 8 12 4.236zM5 9.618l6 3.382v7L5 16.618V9.618zm14 7L13 20v-7l6-3.382v7zM17 13h-2v2h-2v2h2v2h2v-2h2v-2h-2v-2z"/>
            </svg>
        }
        label="New Scene"
        dashed
        brandOnHover
        onClick={onClick}
    />
);

export const PublishCard: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <EntityCard
        thumbnailPlaceholder={
            <svg width="36" height="36" viewBox="0 0 24 24" fill="currentColor" style={ICON_COLOR}>
                <path d="M11.47 1.72a.75.75 0 011.06 0l3.5 3.5a.75.75 0 01-1.06 1.06L12.75 4.06V14.5a.75.75 0 01-1.5 0V4.06L9.03 6.28a.75.75 0 01-1.06-1.06l3.5-3.5zM4.5 14.75a.75.75 0 00-1.5 0v3.5C3 19.769 4.231 21 5.75 21h12.5c1.519 0 2.75-1.231 2.75-2.75v-3.5a.75.75 0 00-1.5 0v3.5c0 .69-.56 1.25-1.25 1.25H5.75c-.69 0-1.25-.56-1.25-1.25v-3.5z"/>
            </svg>
        }
        label="Publish Asset"
        dashed
        brandOnHover
        onClick={onClick}
    />
);
